import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Badge,
  Avatar,
  Typography,
  Switch
} from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
  UserOutlined,
  SettingOutlined,
  PoweroffOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ApiOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useAuthStore } from '@/store/auth'
import { merchantRoomApi } from '@/services/api'

const { Search } = Input
const { Option } = Select
const { Title } = Typography

interface Merchant {
  id: number
  merchant_id: string
  company_name: string
}

interface Venue {
  id: number
  venue_name: string
  venue_type: string
  address: string
  city: string
  status: string
}

interface Room {
  id: number
  uuid: string
  room_id: string
  room_name: string
  room_number?: string
  room_type?: string
  description?: string
  min_capacity: number
  max_capacity: number
  equipment?: any
  features?: string[]
  supports_ai_world: boolean
  ai_device_list?: any
  status: string
  venue_id: number
  created_at: string
  updated_at: string
  // 关联数据
  venue?: Venue
}

const RoomManagement: React.FC = () => {
  const { token } = useAuthStore()

  // 状态管理
  const [rooms, setRooms] = useState<Room[]>([])
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [venues, setVenues] = useState<Venue[]>([])
  const [loading, setLoading] = useState(false)
  const [merchantsLoading, setMerchantsLoading] = useState(false)
  const [venuesLoading, setVenuesLoading] = useState(false)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  // const [searchText, setSearchText] = useState('')
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(null)
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRoom, setEditingRoom] = useState<Room | null>(null)
  const [roomStats, setRoomStats] = useState({
    total: 0,
    available: 0,
    occupied: 0,
    maintenance: 0,
    ai_rooms: 0
  })

  // 房间数据一致性状态
  const [consistencyData, setConsistencyData] = useState<{[key: number]: any}>({})
  const [loadingConsistency, setLoadingConsistency] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })

  const [roomForm] = Form.useForm()

  // 获取商家列表（二级联动）
  const fetchMerchants = async () => {
    setMerchantsLoading(true)
    try {
      const response = await merchantRoomApi.getMerchantsForRooms()
      const merchantList = response.data?.items || []
      setMerchants(merchantList)
    } catch (error) {
      console.error('获取商户列表失败:', error)
      message.error('获取商户列表失败')
    } finally {
      setMerchantsLoading(false)
    }
  }

  // 获取场馆列表（二级联动）
  const fetchVenues = async (merchantId: number) => {
    if (!merchantId) {
      setVenues([])
      return
    }

    // 切换商家时重置分页到第1页
    setPagination(prev => ({ ...prev, current: 1, total: 0 }))

    setVenuesLoading(true)
    try {
      const response = await merchantRoomApi.getVenuesByMerchant(merchantId)
      const venueList = response.data?.items || []
      setVenues(venueList)
    } catch (error) {
      console.error('获取场馆列表失败:', error)
      message.error('获取场馆列表失败')
      setVenues([])
    } finally {
      setVenuesLoading(false)
    }
  }

  // 获取房间列表（按场馆）
  const fetchRooms = async (venueId: number) => {
    if (!venueId) {
      setRooms([])
      return
    }

    setLoading(true)
    try {
      const response = await merchantRoomApi.getRoomsByVenue(venueId, {
        page: pagination.current,
        page_size: pagination.pageSize,
        include_deleted: includeDeleted,
      })
      const roomList = response.data?.items || []
      const total = response.data?.total || 0
      setRooms(roomList)
      setPagination(prev => ({ ...prev, total }))
      updateRoomStats(roomList)

      // Auto fetch data consistency status for AI rooms
      const aiRoomIds = roomList.filter((room: Room) => room.supports_ai_world).map((room: Room) => room.id)
      if (aiRoomIds.length > 0) {
        setTimeout(() => fetchRoomConsistency(aiRoomIds), 500) // Delay 500ms to avoid too many simultaneous requests
      }
    } catch (error) {
      console.error('获取房间列表失败:', error)
      message.error('获取房间列表失败')
      setRooms([])
    } finally {
      setLoading(false)
    }
  }

  // 更新房间统计数据
  const updateRoomStats = (rooms: Room[]) => {
    const stats = {
      total: rooms.length,
      available: rooms.filter(r => r.status === 'available').length,
      occupied: rooms.filter(r => r.status === 'occupied').length,
      maintenance: rooms.filter(r => r.status === 'maintenance').length,
      ai_rooms: rooms.filter(r => r.supports_ai_world).length
    }
    setRoomStats(stats)
  }

  useEffect(() => {
    fetchMerchants()
  }, [])

  useEffect(() => {
    if (selectedMerchantId) {
      fetchVenues(selectedMerchantId)
    } else {
      setVenues([])
      setSelectedVenueId(null)
      setRooms([])
    }
  }, [selectedMerchantId])

  useEffect(() => {
    if (selectedVenueId) {
      fetchRooms(selectedVenueId)
    } else {
      setRooms([])
    }
  }, [selectedVenueId, includeDeleted, pagination.current])

  // 获取房间数据一致性状态
  const fetchRoomConsistency = async (roomIds: number[]) => {
    if (roomIds.length === 0) return

    setLoadingConsistency(true)
    try {
      const response = await merchantRoomApi.batchCheckRoomsConsistency(roomIds)
      const data = response.data
      const consistencyMap: {[key: number]: any} = {}

      if (data.rooms && Array.isArray(data.rooms)) {
        data.rooms.forEach((room: any) => {
          consistencyMap[room.room_id] = room
        })
      }

      setConsistencyData(consistencyMap)
    } catch (error) {
      console.error('获取房间一致性状态失败:', error)
    } finally {
      setLoadingConsistency(false)
    }
  }

  // 强制关闭房间
  const forceCloseRoom = async (roomId: number) => {
    try {
      const response = await merchantRoomApi.forceCloseRoom(roomId)
      message.success(response.data?.message || '房间强制关闭成功')

      // 重新获取房间列表和一致性状态
      if (selectedVenueId) {
        fetchRooms(selectedVenueId)
      }
    } catch (error) {
      console.error('强制关闭房间失败:', error)
      message.error('强制关闭房间失败: ' + (error instanceof Error ? error.message : '未知错误'))
    }
  }

  // 处理商家选择
  const handleMerchantChange = (merchantId: number) => {
    setSelectedMerchantId(merchantId)
    setSelectedVenueId(null)
    setRooms([])
  }

  // 处理场馆选择
  const handleVenueChange = (venueId: number) => {
    setSelectedVenueId(venueId)
  }

  // 处理编辑房间
  const handleEditRoom = (room: Room) => {
    setEditingRoom(room)
    roomForm.setFieldsValue({
      venue_id: room.venue_id,
      room_name: room.room_name,
      room_number: room.room_number,
      room_type: room.room_type,
      min_capacity: room.min_capacity,
      max_capacity: room.max_capacity,
      description: room.description,
      supports_ai_world: room.supports_ai_world,
      status: room.status,
    })
    setModalVisible(true)
  }

  // 处理删除房间
  const handleDeleteRoom = async (roomId: number) => {
    if (!token) {
      message.error('未登录')
      return
    }

    try {
      await merchantRoomApi.deleteRoom(roomId)
      message.success('房间删除成功')
      if (selectedVenueId) {
        fetchRooms(selectedVenueId)
      }
    } catch (error: any) {
      console.error('删除房间失败:', error)
      message.error(error.response?.data?.message || error.message || '删除失败')
    }
  }

  // 处理释放房间
  const handleReleaseRoom = async (room: Room) => {
    if (!token) {
      message.error('未登录')
      return
    }

    try {
      const response = await fetch(`/api/v1/merchant/rooms/${room.id}/release`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      const responseData = await response.json()

      // ✅ 适配新的统一响应格式：{ error_code: 0, message: "...", data: {...} }
      if (responseData.error_code !== 0) {
        message.error(responseData.message || '释放房间失败')
        return
      }

      const data = responseData.data
      message.success(`房间释放成功，已停止 ${data.released_count} 个AI世界实例`)
      if (selectedVenueId) {
        fetchRooms(selectedVenueId)
      }
    } catch (error) {
      console.error('释放房间失败:', error)
      message.error('网络错误，请重试')
    }
  }

  // 处理切换房间状态
  const handleToggleRoomStatus = async (room: Room) => {
    if (!token) {
      message.error('未登录')
      return
    }

    const newStatus = room.status === 'available' ? 'maintenance' : 'available'

    try {
      await merchantRoomApi.updateRoom(room.id, { status: newStatus })
      message.success(`房间状态已更新为${newStatus === 'available' ? '可用' : '维护中'}`)
      if (selectedVenueId) {
        fetchRooms(selectedVenueId)
      }
    } catch (error: any) {
      console.error('切换状态失败:', error)
      message.error(error.response?.data?.message || error.message || '操作失败')
    }
  }

  // 获取房间状态颜色
  const getRoomStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success'
      case 'occupied': return 'processing'
      case 'maintenance': return 'warning'
      case 'offline': return 'error'
      default: return 'default'
    }
  }

  // 获取房间状态文本
  const getRoomStatusText = (status: string) => {
    switch (status) {
      case 'available': return '可用'
      case 'occupied': return '使用中'
      case 'maintenance': return '维护中'
      case 'offline': return '离线'
      default: return status
    }
  }

  const roomColumns: ColumnsType<Room> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <Typography.Text copyable>{id}</Typography.Text>
    },
    {
      title: '房间信息',
      key: 'info',
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar
            size={48}
            icon={<HomeOutlined />}
            className="mr-3"
            style={{ backgroundColor: record.supports_ai_world ? '#722ed1' : '#1890ff' }}
          />
          <div>
            <div className="font-medium">[{record.id}] {record.room_name}</div>
            <div className="text-sm text-gray-500">
              <Tag color={record.room_type === 'vip' ? 'gold' : 'default'}>
                {record.room_type || '标准'}
              </Tag>
              {record.room_number && (
                <Tag>{record.room_number}</Tag>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      title: '容量',
      key: 'capacity',
      render: (_, record) => (
        <div>
          <div className="flex items-center mb-1">
            <UserOutlined className="mr-2 text-gray-400" />
            <span>{record.min_capacity}-{record.max_capacity} 人</span>
          </div>
        </div>
      )
    },
    {
      title: 'AI支持',
      key: 'ai_support',
      render: (_, record) => (
        record.supports_ai_world ? (
          <Tag color="purple">支持AI世界</Tag>
        ) : (
          <Tag color="default">不支持</Tag>
        )
      )
    },
    {
      title: '设备',
      key: 'equipment',
      render: (_, record) => (
        <div>
          {record.equipment ? (
            <Tag color="blue" icon={<SettingOutlined />}>已配置</Tag>
          ) : (
            <Tag color="default">未配置</Tag>
          )}
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Badge
          status={getRoomStatusColor(status) as any}
          text={getRoomStatusText(status)}
        />
      )
    },
    {
      title: '数据一致性',
      key: 'consistency',
      render: (_, record) => {
        const consistency = consistencyData[record.id]

        if (!record.supports_ai_world) {
          return <Tag color="default">No AI</Tag>
        }

        if (!consistency) {
          return (
            <Button
              type="link"
              size="small"
              icon={<ApiOutlined />}
              loading={loadingConsistency}
              onClick={() => fetchRoomConsistency([record.id])}
            >
              Check Consistency
            </Button>
          )
        }

        if (consistency.is_consistent) {
          return (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Normal ({consistency.running_instances})
            </Tag>
          )
        } else {
          return (
            <Space direction="vertical" size="small">
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                Inconsistent
              </Tag>
              <Space size="small">
                <Tag color="warning">Instances: {consistency.running_instances}</Tag>
                <Tag color="warning">Scenes: {consistency.scene_data_count}</Tag>
              </Space>
              <Button
                size="small"
                type="primary"
                danger
                icon={<PoweroffOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Force Close Room',
                    content: 'Are you sure you want to force close this room? This will stop all running world instances.',
                    okText: 'Confirm',
                    cancelText: 'Cancel',
                    okButtonProps: { danger: true },
                    onOk: () => forceCloseRoom(record.id),
                  })
                }}
              >
                Force Close
              </Button>
            </Space>
          )
        }
      }
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      dataIndex: 'action',
      render: (_, record) => (
        <Space size="middle">
          {record.supports_ai_world && (
            <Button
              type="link"
              size="small"
              icon={<PoweroffOutlined />}
              onClick={() => handleReleaseRoom(record)}
            >
              释放房间
            </Button>
          )}
          <Button
            type="link"
            size="small"
            onClick={() => handleToggleRoomStatus(record)}
          >
            {record.status === 'available' ? '维护' : '启用'}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditRoom(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个房间吗？"
            onConfirm={() => handleDeleteRoom(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">房间管理</h1>
        <p className="text-gray-600">管理场馆内的所有房间信息，支持AI世界设备配置和房间状态管理</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="总房间数"
              value={roomStats.total}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="可用房间"
              value={roomStats.available}
              valueStyle={{ color: '#389e0d' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="使用中"
              value={roomStats.occupied}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="AI房间"
              value={roomStats.ai_rooms}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 二级联动筛选 */}
      <Card className="mb-6">
        <div className="flex gap-4 mb-4">
          <div style={{ width: 300 }}>
            <label className="block text-sm font-medium mb-2">选择商家</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={selectedMerchantId || ''}
              onChange={(e) => handleMerchantChange(Number(e.target.value))}
              disabled={merchantsLoading}
            >
              <option value="">请选择商家</option>
              {Array.isArray(merchants) && merchants.map(merchant => (
                <option key={merchant.id} value={merchant.id}>
                  [{merchant.id}] {merchant.company_name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: 300 }}>
            <label className="block text-sm font-medium mb-2">选择场馆</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={selectedVenueId || ''}
              onChange={(e) => handleVenueChange(Number(e.target.value))}
              disabled={!selectedMerchantId || venuesLoading}
            >
              <option value="">请选择场馆</option>
              {Array.isArray(venues) && venues.map(venue => (
                <option key={venue.id} value={venue.id}>
                  [{venue.id}] {venue.venue_name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              size="small"
              checked={includeDeleted}
              onChange={setIncludeDeleted}
            />
            <span className="text-sm text-gray-500">显示已删除</span>
          </div>
        </div>

        {/* 房间列表 */}
        {selectedVenueId ? (
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-4">
              <Search
                placeholder="搜索房间名称"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                // onSearch={setSearchText}
              />
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingRoom(null)
                roomForm.resetFields()
                roomForm.setFieldsValue({ venue_id: selectedVenueId })
                setModalVisible(true)
              }}
            >
              新建房间
            </Button>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            请先选择商家和场馆
          </div>
        )}

        {selectedVenueId && (
          <Table
            columns={roomColumns}
            dataSource={rooms}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ current: page, pageSize: pageSize || 10, total: prev.total }))
              },
            }}
          />
        )}
      </Card>

      {/* 房间模态框 */}
      <Modal
        title={
          <span>
            {editingRoom ? '编辑房间' : '添加房间'}
            {editingRoom && (
              <Typography.Text type="secondary" className="ml-3 text-sm">
                ID: {editingRoom.id}
              </Typography.Text>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingRoom(null)
          roomForm.resetFields()
        }}
        width={600}
        footer={null}
      >
        <Form
          form={roomForm}
          layout="vertical"
          onFinish={async (formValues) => {
            try {
              console.log('房间数据:', formValues)

              if (!token) {
                message.error('未登录，请先登录')
                return
              }

              const isEditing = !!editingRoom
              const url = isEditing
                ? `/api/v1/merchant/rooms/${editingRoom.id}`
                : '/api/v1/merchant/rooms'
              const method = isEditing ? 'PUT' : 'POST'

              const requestBody: any = {
                venue_id: formValues.venue_id,
                room_name: formValues.room_name,
                room_number: formValues.room_number,
                room_type: formValues.room_type,
                min_capacity: formValues.min_capacity,
                max_capacity: formValues.max_capacity,
                description: formValues.description,
                supports_ai_world: formValues.supports_ai_world,
              }

              // 只有创建时才需要 merchant_id
              if (!isEditing && selectedMerchantId) {
                requestBody.merchant_id = selectedMerchantId
              }

              // 如果是编辑且有状态字段
              if (isEditing && formValues.status) {
                requestBody.status = formValues.status
              }

              const response = await fetch(url, {
                method,
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
              })

              const responseData = await response.json()

              // ✅ 适配新的统一响应格式：{ error_code: 0, message: "...", data: {...} }
              if (responseData.error_code !== 0) {
                message.error(responseData.message || '操作失败')
                return
              }

              message.success(isEditing ? '房间更新成功' : '房间创建成功')
              setModalVisible(false)
              setEditingRoom(null)
              roomForm.resetFields()
              if (selectedVenueId) {
                fetchRooms(selectedVenueId)
              }
            } catch (error) {
              console.error('房间操作失败:', error)
              message.error('网络错误，请重试')
            }
          }}
        >
          {/* 商家选择（仅创建模式显示） */}
          {!editingRoom && (
            <Form.Item
              label="选择商家"
              name="merchant_id"
              rules={[{ required: true, message: '请选择商家' }]}
            >
              <Select
                placeholder="选择商家"
                onChange={(value) => {
                  fetchVenues(value)
                  roomForm.setFieldsValue({ venue_id: undefined })
                }}
                disabled={merchantsLoading}
              >
                {Array.isArray(merchants) && merchants.map(merchant => (
                  <Option key={merchant.id} value={merchant.id}>
                    [{merchant.id}] {merchant.company_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* 场馆选择（仅创建模式显示） */}
          {!editingRoom && (
            <Form.Item
              label="选择场馆"
              name="venue_id"
              rules={[{ required: true, message: '请选择场馆' }]}
            >
              <Select
                placeholder="请先选择商家"
                disabled={!roomForm.getFieldValue('merchant_id') || venuesLoading}
              >
                {Array.isArray(venues) && venues.map(venue => (
                  <Option key={venue.id} value={venue.id}>
                    [{venue.id}] {venue.venue_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* 房间名称 */}
          <Form.Item
            label="房间名称"
            name="room_name"
            rules={[{ required: true, message: '请输入房间名称' }]}
          >
            <Input placeholder="输入房间名称" />
          </Form.Item>

          <Form.Item
            label="房间编号"
            name="room_number"
          >
            <Input placeholder="输入房间编号（如A101）" />
          </Form.Item>

          <Form.Item
            label="房间类型"
            name="room_type"
          >
            <Select placeholder="选择房间类型">
              <Option value="standard">标准房间</Option>
              <Option value="vip">VIP房间</Option>
              <Option value="conference">会议室</Option>
              <Option value="theater">剧场</Option>
              <Option value="hall">大厅</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="最小容量"
                name="min_capacity"
                rules={[{ required: true, message: '请输入最小容量' }]}
              >
                <Input type="number" min={1} placeholder="最小人数" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="最大容量"
                name="max_capacity"
                rules={[{ required: true, message: '请输入最大容量' }]}
              >
                <Input type="number" min={1} placeholder="最大人数" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="描述"
            name="description"
          >
            <Input.TextArea rows={3} placeholder="输入房间描述" />
          </Form.Item>

          <Form.Item
            label="支持AI世界"
            name="supports_ai_world"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {/* 状态选择（仅编辑模式显示） */}
          {editingRoom && (
            <Form.Item
              label="房间状态"
              name="status"
            >
              <Select placeholder="选择房间状态">
                <Option value="available">可用</Option>
                <Option value="occupied">使用中</Option>
                <Option value="maintenance">维护中</Option>
                <Option value="offline">离线</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRoom ? '更新' : '创建'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false)
                setEditingRoom(null)
                roomForm.resetFields()
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default RoomManagement
